import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Key, Plus, Trash2, Copy, RefreshCw, CheckCircle, Clock, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  CreateApiKeyResponse,
  ListApiKeysResponse,
  RevokeApiKeyResponse,
  RotateApiKeyResponse,
} from "@workspace/api-zod";
import type { ApiKey } from "@workspace/api-client-react";

const ALL_SCOPES = [
  { value: "goals:read", label: "Goals - Read" },
  { value: "goals:write", label: "Goals - Write" },
  { value: "tasks:read", label: "Tasks - Read" },
  { value: "tasks:write", label: "Tasks - Write" },
  { value: "habits:read", label: "Habits - Read" },
  { value: "journal:read", label: "Journal - Read" },
  { value: "calendar:read", label: "Calendar - Read" },
];

export default function ApiKeysPage() {
  const { user } = useAuth();
  const { toast } = useToast();

  const [createOpen, setCreateOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyScopes, setNewKeyScopes] = useState<string[]>(["goals:read"]);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [createdKeyName, setCreatedKeyName] = useState<string>("");
  const [copied, setCopied] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);
  const [rotateResult, setRotateResult] = useState<{ name: string; key: string } | null>(null);

  // API key responses are parsed with the generated zod schemas so any
  // server shape change fails loudly instead of rendering garbage.
  const { data: keys = [], isLoading } = useQuery({
    queryKey: ["/api/api-keys"],
    select: (data: unknown): ApiKey[] => ListApiKeysResponse.parse(data),
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/api-keys", { name: newKeyName.trim(), scopes: newKeyScopes });
      return CreateApiKeyResponse.parse(await res.json());
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/api-keys"] });
      setCreateOpen(false);
      setCreatedKey(data.plaintext);
      setCreatedKeyName(data.name);
      setNewKeyName("");
      setNewKeyScopes(["goals:read"]);
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const revokeMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("DELETE", `/api/api-keys/${id}`);
      return RevokeApiKeyResponse.parse(await res.json());
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/api-keys"] });
      setRevokeTarget(null);
      toast({ title: "API key revoked" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const rotateMutation = useMutation({
    mutationFn: async (key: ApiKey) => {
      const res = await apiRequest("POST", `/api/api-keys/${key.id}/rotate`);
      return RotateApiKeyResponse.parse(await res.json());
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/api-keys"] });
      setRotateResult({ name: data.name, key: data.plaintext });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleScope = (scope: string) => {
    setNewKeyScopes(prev => prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]);
  };

  return (
    <div className="w-full space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Key className="w-8 h-8" />
            API Keys
          </h1>
          <p className="text-muted-foreground mt-1">
            Generate API keys for programmatic access to your data. Keys are shown only once at creation.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create API Key
        </Button>
      </div>

      <Card>
        {isLoading ? (
          <div className="p-6 space-y-3">{[1,2,3].map(i=><div key={i} className="h-12 bg-muted animate-pulse rounded"/>)}</div>
        ) : keys.length === 0 ? (
          <div className="p-12 text-center">
            <Key className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No API keys yet. Create one to get started.</p>
            <Button className="mt-4" onClick={() => setCreateOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />Create API Key
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Prefix</TableHead>
                  <TableHead>Scopes</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys.map(k => (
                  <TableRow key={k.id}>
                    <TableCell className="font-medium">{k.name}</TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-2 py-1 rounded font-mono">{k.keyPrefix}••••••••</code>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {k.scopes.map(s => <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>)}
                        {k.scopes.length === 0 && <span className="text-xs text-muted-foreground">None</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {k.lastUsedAt ? format(new Date(k.lastUsedAt), "MMM d, yyyy HH:mm") : "Never"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {k.expiresAt ? (
                        <span className={new Date(k.expiresAt) < new Date() ? "text-destructive" : ""}>
                          {format(new Date(k.expiresAt), "MMM d, yyyy")}
                        </span>
                      ) : "Never"}
                    </TableCell>
                    <TableCell>
                      {k.isActive ? (
                        <Badge className="bg-green-500 hover:bg-green-600"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>
                      ) : (
                        <Badge variant="destructive">Revoked</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {k.isActive && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => rotateMutation.mutate(k)} disabled={rotateMutation.isPending} title="Rotate key">
                              <RefreshCw className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => setRevokeTarget(k)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="w-screen h-screen m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>
              Give your key a descriptive name and select the permissions it should have.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="keyName">Key Name *</Label>
              <Input
                id="keyName"
                placeholder="e.g. Production Integration"
                value={newKeyName}
                onChange={e => setNewKeyName(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Scopes (Permissions)</Label>
              <div className="mt-2 space-y-2 border rounded-lg p-3">
                {ALL_SCOPES.map(s => (
                  <div key={s.value} className="flex items-center gap-2">
                    <Checkbox
                      id={s.value}
                      checked={newKeyScopes.includes(s.value)}
                      onCheckedChange={() => toggleScope(s.value)}
                    />
                    <Label htmlFor={s.value} className="cursor-pointer font-normal">{s.label}</Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={() => createMutation.mutate()} disabled={!newKeyName.trim() || createMutation.isPending}>
              {createMutation.isPending ? "Creating..." : "Create Key"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Show New Key Dialog */}
      <Dialog open={!!createdKey} onOpenChange={open => { if (!open) { setCreatedKey(null); setCopied(false); } }}>
        <DialogContent >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              API Key Created
            </DialogTitle>
            <DialogDescription>
              This is the only time your full API key will be shown. Copy it now and store it safely.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium mb-1">Key Name: <span className="font-normal">{createdKeyName}</span></p>
            </div>
            <div className="relative">
              <code className="block text-xs bg-muted p-3 rounded-lg break-all font-mono leading-relaxed pr-12">
                {createdKey}
              </code>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-2 right-2"
                onClick={() => copyToClipboard(createdKey!)}
              >
                {copied ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <div className="flex items-start gap-2 text-sm text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <p>Store this key securely. It won't be shown again. Use it as: <code className="text-xs bg-orange-100 dark:bg-orange-900/40 px-1 rounded">Authorization: Bearer YOUR_KEY</code></p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => { setCreatedKey(null); setCopied(false); }}>
              I've copied this key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Show Rotated Key Dialog */}
      <Dialog open={!!rotateResult} onOpenChange={open => { if (!open) { setRotateResult(null); setCopied(false); } }}>
        <DialogContent >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-blue-500" />
              Key Rotated
            </DialogTitle>
            <DialogDescription>
              Your key has been rotated. Copy the new key — the old one is now invalid.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <code className="block text-xs bg-muted p-3 rounded-lg break-all font-mono pr-12">
                {rotateResult?.key}
              </code>
              <Button size="sm" variant="ghost" className="absolute top-2 right-2" onClick={() => copyToClipboard(rotateResult?.key ?? "")}>
                {copied ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => { setRotateResult(null); setCopied(false); }}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Confirm */}
      <AlertDialog open={!!revokeTarget} onOpenChange={() => setRevokeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke API Key?</AlertDialogTitle>
            <AlertDialogDescription>
              Revoking <strong>{revokeTarget?.name}</strong> will immediately disable all requests using it. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => revokeTarget && revokeMutation.mutate(revokeTarget.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Revoke Key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
